import {
  DateTimePicker,
  Dialog,
  EMPTY_ARRAY,
  FormGroup,
  HStack,
  Icon,
  IconName,
  Input,
  LedgerUpdateTypeEnum,
  NOW,
  NotificationVariants,
  NumberInput,
  ProductTypeEnum,
  SearchSelect,
  Tooltip,
  includesEnumValueForSet,
  prettifyLedgerUpdateTypeEnum,
  productTypeLabel,
  toBigWithDefault,
  useAssetsContext,
  useCurrenciesContext,
  useDynamicCallback,
  useGlobalToasts,
  useMarketAccountsContext,
  type DialogProps,
} from '@talos/kyoko';
import { capitalize, identity, isEmpty, keys } from 'lodash-es';
import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { useSymbolSelectorWidth } from '../../../components/SymbolSelector/useSymbolSelectorWidth';
import { PositionTypeEnum, usePositionRequests, type UpdatePositionParams } from '../../../hooks';
import { useSubAccounts } from '../../../providers';
import { useUpdateBalancesAccounts } from '../BalancesV2/useUpdateBalancesAccounts';
import type { UpdatePositionForm } from './types';
import { useUpdatePositionValidation } from './useUpdatePositionValidation';

export type UpdatePositionPrimer = {
  SubAccount?: string;
  MarketAccount?: string;
  Asset?: string;
  AvgCostCurrency?: string;
  AvgCost?: string;
};

interface UpdatePositionsDialogProps {
  dialogProps: DialogProps;
  /** Either MarketAccountPosition or SubAccountPosition */
  positionType: PositionTypeEnum;
  /** A set of defaults to open the dialog with pre-selected */
  positionPrimer?: UpdatePositionPrimer;
  /** For the Market Account case, you are able to pass custom market account options, taking full control of what is selectable */
  customMarketAccountOptions?: string[];
  /**
   * We sometimes use this dialog to update "balances". Balances can update as a result of pushing a position update. Setting this to true
   * just changes the terminology used within the dialog to not confuse the user. No functionality change. Defaults to false.
   */
  useBalancesTerminology?: boolean;
}

const updateTypeOptions = [
  LedgerUpdateTypeEnum.Trade,
  LedgerUpdateTypeEnum.TradingFee,
  LedgerUpdateTypeEnum.TradeCancel,
  LedgerUpdateTypeEnum.TradeAmend,
  LedgerUpdateTypeEnum.FundingPnLDelta,
  LedgerUpdateTypeEnum.RealizedPnLDelta,
  LedgerUpdateTypeEnum.Deposit,
  LedgerUpdateTypeEnum.Withdrawal,
  LedgerUpdateTypeEnum.TransferFee,
  LedgerUpdateTypeEnum.ReconAdjustment,
  LedgerUpdateTypeEnum.BalanceDelta,
  LedgerUpdateTypeEnum.Delivery,
  LedgerUpdateTypeEnum.StakingReward,
  LedgerUpdateTypeEnum.Settlement,
  LedgerUpdateTypeEnum.PreliminaryCreate,
  LedgerUpdateTypeEnum.FinalCreate,
  LedgerUpdateTypeEnum.PreliminaryRedeem,
  LedgerUpdateTypeEnum.FinalRedeem,
  LedgerUpdateTypeEnum.ManagementFee,
  LedgerUpdateTypeEnum.DepositInterest,
];

const negativeUpdateTypes = [
  LedgerUpdateTypeEnum.Withdrawal,
  LedgerUpdateTypeEnum.TradingFee,
  LedgerUpdateTypeEnum.TransferFee,
  LedgerUpdateTypeEnum.PreliminaryRedeem,
  LedgerUpdateTypeEnum.FinalRedeem,
  LedgerUpdateTypeEnum.ManagementFee,
];

const negativeUpdateTypesSet = new Set(negativeUpdateTypes);

function getUpdateTypeLabel(updateType: LedgerUpdateTypeEnum): string {
  return prettifyLedgerUpdateTypeEnum(updateType);
}

export const UpdatePositionsDialog = ({
  dialogProps,
  positionType,
  positionPrimer,
  customMarketAccountOptions,
  useBalancesTerminology,
}: UpdatePositionsDialogProps) => {
  const { add: addToast } = useGlobalToasts();
  const { updatePosition } = usePositionRequests();
  const { allSubAccountBooks, subAccountsByName } = useSubAccounts();
  const { assetsList, getAssetDisplaySymbol, resolveSecurityOrCurrency } = useAssetsContext();
  const { currenciesList } = useCurrenciesContext();
  const { marketAccountsByMarket, marketAccountsByName } = useMarketAccountsContext();
  const { accounts: internalMarketAccountNames } = useUpdateBalancesAccounts();
  const [form, setForm] = useState<UpdatePositionForm>({});

  const balanceOrPosition = useBalancesTerminology ? `balance` : `position`;

  // Get the width we should be using for the symbol selector dropdown
  const symbolSelectorDropdownWidth = useSymbolSelectorWidth();

  // This useEffect performs priming of the form based on a possibly passed positionPrimer.
  // Whenever we switch to open, or the positionPrimer changes, we reset the form to the initial (maybe primed) state.
  useEffect(() => {
    if (dialogProps.isOpen) {
      setForm({
        // Attempt to prime, if there is no positionPrimer this just becomes an empty object which is fine
        updateType: undefined,
        subAccount: positionPrimer?.SubAccount,
        marketAccount: positionPrimer?.MarketAccount,
        asset: positionPrimer?.Asset,
        avgCostCurrency: positionPrimer?.AvgCostCurrency,
        avgCost: positionPrimer?.AvgCost,
      });
    }
  }, [dialogProps.isOpen, positionPrimer]);

  const { errors, touched, setTouched, setAllTouched } = useUpdatePositionValidation(form, positionType);

  useEffect(() => {
    // When the dialog is closed, we clear the form.
    if (!dialogProps.isOpen) {
      setForm({});
      setTouched({});
    }
  }, [dialogProps.isOpen, setTouched]);

  const updateForm = useDynamicCallback((update: Partial<UpdatePositionForm>) => {
    setForm(prev => ({ ...prev, ...update }));

    // Set touched after updates run to not hit any weird annoying timing ux issues (just put at back of queue)
    setTimeout(() => {
      setTouched(prev => {
        // Map each update to just true, then apply to the touched state
        const update: Record<string, boolean> = {};
        keys(update).forEach(key => {
          update[key] = true;
        });
        return { ...prev, ...update };
      });
    }, 0);
  });

  const subAccountOptions = useMemo(() => allSubAccountBooks?.map(s => s.Name) ?? [], [allSubAccountBooks]);
  const marketAccountOptions = useMemo(() => {
    if (customMarketAccountOptions) {
      return customMarketAccountOptions;
    }
    if (internalMarketAccountNames?.length) {
      return internalMarketAccountNames;
    }
    return marketAccountsByMarket.get('internal')?.map(ma => ma.Name) ?? [];
  }, [customMarketAccountOptions, internalMarketAccountNames, marketAccountsByMarket]);
  const assetOptions = useMemo(() => assetsList.map(a => a.Symbol), [assetsList]);
  const currencyOptions = useMemo(() => currenciesList.map(c => c.Symbol), [currenciesList]);

  const getAssetGroup = useDynamicCallback((asset: string) => {
    const { currency, security } = resolveSecurityOrCurrency(asset);
    if (currency) {
      return productTypeLabel(ProductTypeEnum.Spot);
    } else if (security) {
      return productTypeLabel(security.ProductType);
    } else {
      return 'Unknown';
    }
  });

  const getAssetDescription = useDynamicCallback((asset: string) => {
    const { currency, security } = resolveSecurityOrCurrency(asset);
    return (currency ?? security)?.Description ?? '';
  });

  const getSubAccountLabel = useDynamicCallback((subAccountName: string | undefined) =>
    subAccountName ? (subAccountsByName?.get(subAccountName)?.DisplayName ?? subAccountName) : (subAccountName ?? '')
  );

  const getMarketAccountLabel = useDynamicCallback((marketAccountName: string | undefined) =>
    marketAccountName
      ? (marketAccountsByName.get(marketAccountName)?.DisplayName ?? marketAccountName)
      : (marketAccountName ?? '')
  );

  const handleChangeUpdateType = useDynamicCallback((updateType?: LedgerUpdateTypeEnum) => {
    updateForm({ updateType });
  });

  const handleChangeSubAccount = useDynamicCallback((subAccount?: string) => {
    updateForm({ subAccount });
  });

  const handleChangeMarketAccount = useDynamicCallback((marketAccount?: string) => {
    updateForm({ marketAccount });
  });

  const handleChangeAsset = useDynamicCallback((asset?: string) => {
    updateForm({ asset });
  });

  const handleChangeAmount = useDynamicCallback((amount?: string) => {
    updateForm({ amount });
  });

  const handleChangeAvgCost = useDynamicCallback((avgCost?: string) => {
    updateForm({ avgCost });
  });

  const handleChangeAvgCostCurrency = useDynamicCallback((avgCostCurrency?: string) => {
    updateForm({ avgCostCurrency });
  });

  const handleTimestampChange = useDynamicCallback((timestamp?: Date | null) => {
    updateForm({ timestamp: timestamp ?? undefined });
  });

  const handleChangeComments = useDynamicCallback((event: ChangeEvent<HTMLInputElement>) => {
    updateForm({ comments: event.target.value });
  });

  const handleConfirm = useDynamicCallback(() => {
    // This should already be the case since the confirm button is disabled otherwise, but do this here too
    const account = positionType === PositionTypeEnum.SubAccountPosition ? form.subAccount : form.marketAccount;
    if (form.updateType == null || account == null || form.asset == null || form.amount == null) {
      return;
    }
    const params: UpdatePositionParams = {
      Type: LedgerUpdateTypeEnum.BalanceDelta,
      IntendedType: form.updateType,
      AccountName: account,
      Asset: form.asset,
      Amount: form.amount,
      AvgCost: form.avgCost,
      AvgCostCurrency: form.avgCostCurrency,
      LastUpdateTime: form.timestamp?.toISOString(),
      Comments: form.comments,
    };

    updatePosition(params, positionType)
      .then(() => {
        addToast({
          text: `Successfully updated ${balanceOrPosition}.`,
          variant: NotificationVariants.Positive,
        });
      })
      .catch((e: ErrorEvent) => {
        addToast({
          text: `Failed to update ${balanceOrPosition}: ${e?.toString()}`,
          variant: NotificationVariants.Negative,
        });
      });
  });

  const showPositiveAmountWarning = useMemo(() => {
    const updateTypeIsNegative =
      form.updateType != null && includesEnumValueForSet(negativeUpdateTypesSet, form.updateType);
    const amountIsPositive = form.amount != null && toBigWithDefault(form.amount, 0).gt(0);

    // Certain update types, such as withdrawals, should always be given a negative amount by the user. This warning informs them if they are
    // performing a positive withdrawal for example, which would _increase_ the balance on the account (which isnt the point of a withdrawal)
    return updateTypeIsNegative && amountIsPositive;
  }, [form.updateType, form.amount]);

  return (
    <Dialog
      {...dialogProps}
      title={
        positionType === PositionTypeEnum.SubAccountPosition
          ? 'Adjust Sub Account Position'
          : `Update ${capitalize(balanceOrPosition)}`
      }
      width={400}
      confirmLabel={`Update ${capitalize(balanceOrPosition)}`}
      confirmDisabled={!isEmpty(errors)}
      onConfirm={handleConfirm}
      onConfirmMouseOver={setAllTouched}
    >
      <FormGroup label="Update Type*" error={touched.updateType && errors.updateType ? errors.updateType : undefined}>
        <SearchSelect
          selection={form.updateType}
          options={updateTypeOptions}
          onChange={handleChangeUpdateType}
          getLabel={getUpdateTypeLabel}
          data-testid="update-position-update-type"
        />
      </FormGroup>

      {/* Either a sub account or market account control depending on the position type we're editing */}
      {positionType === PositionTypeEnum.SubAccountPosition ? (
        <FormGroup label="Sub Account*" error={touched.subAccount && errors.subAccount ? errors.subAccount : undefined}>
          <SearchSelect
            selection={form.subAccount}
            options={subAccountOptions ?? EMPTY_ARRAY}
            onChange={handleChangeSubAccount}
            getLabel={getSubAccountLabel}
            data-testid="update-position-sub-account"
          />
        </FormGroup>
      ) : (
        <FormGroup
          label="Market Account*"
          error={touched.marketAccount && errors.marketAccount ? errors.marketAccount : undefined}
        >
          <SearchSelect
            selection={form.marketAccount}
            options={marketAccountOptions ?? EMPTY_ARRAY}
            onChange={handleChangeMarketAccount}
            getLabel={getMarketAccountLabel}
            data-testid="update-position-market-account"
          />
        </FormGroup>
      )}

      <HStack gap="spacing16" alignItems="flex-start">
        <FormGroup label="Asset*" flexBasis="50%" error={touched.asset && errors.asset ? errors.asset : undefined}>
          <SearchSelect
            selection={form.asset}
            options={assetOptions}
            onChange={handleChangeAsset}
            getGroup={getAssetGroup}
            showGroupTabs
            getLabel={getAssetDisplaySymbol}
            getDescription={getAssetDescription}
            data-testid="update-position-asset"
            dropdownWidth={symbolSelectorDropdownWidth}
            maxHeight={400}
          />
        </FormGroup>

        <FormGroup
          label="Amount*"
          flexBasis="50%"
          error={touched.amount && errors.amount ? errors.amount : undefined}
          tooltip="Positive amounts will increase the balance. Enter a negative number to decrease the balance (e.g., for withdrawals)."
          labelSuffix={
            showPositiveAmountWarning && (
              <Tooltip tooltip="Enter a negative amount to reduce the balance. Positive amounts will increase it.">
                <Icon icon={IconName.ExclamationSolid} color="colors.yellow.lighten" />
              </Tooltip>
            )
          }
        >
          <NumberInput value={form.amount ?? ''} onChange={handleChangeAmount} data-testid="update-position-amount" />
        </FormGroup>
      </HStack>

      <HStack gap="spacing16" alignItems="flex-start">
        <FormGroup
          label="Avg Cost Currency"
          flexBasis="50%"
          error={touched.avgCostCurrency && errors.avgCostCurrency ? errors.avgCostCurrency : undefined}
        >
          <SearchSelect
            selection={form.avgCostCurrency}
            options={currencyOptions}
            onChange={handleChangeAvgCostCurrency}
            getLabel={identity}
            showClear
            data-testid="update-position-avg-cost-currency"
          />
        </FormGroup>

        <FormGroup
          label="Avg Cost"
          flexBasis="50%"
          error={touched.avgCost && errors.avgCost ? errors.avgCost : undefined}
        >
          <NumberInput
            value={form.avgCost ?? ''}
            onChange={handleChangeAvgCost}
            data-testid="update-position-avg-cost"
          />
        </FormGroup>
      </HStack>

      <FormGroup label="Update Timestamp" error={touched.timestamp && errors.timestamp ? errors.timestamp : undefined}>
        <DateTimePicker
          value={form.timestamp ?? null}
          onChange={handleTimestampChange}
          portalize={false} // renders behind modal otherwise
          showMilliseconds
          shortcuts={{ [NOW]: 'Now' }}
        />
      </FormGroup>

      <FormGroup
        mb="spacing16"
        label="Comments*"
        error={touched.comments && errors.comments ? errors.comments : undefined}
      >
        <Input
          value={form.comments ?? ''}
          autoComplete="off"
          onChange={handleChangeComments}
          data-testid="update-position-comments"
        />
      </FormGroup>
    </Dialog>
  );
};
